import {
  ClockCircleOutlined,
  ColumnWidthOutlined,
  CommentOutlined,
  FilterOutlined,
  FileTextOutlined,
  PlusOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  Empty,
  Flex,
  Grid,
  Input,
  List,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import type {
  EstadoDoItem,
  ExamModule,
  QuestionStatus,
  QuestionSummary,
} from '@titularis/api-client';
import { api } from '../../api';
import { useSession } from '../../auth';
import {
  ESTADO_DA_LISTA_COR,
  ESTADO_DA_LISTA_LABEL,
  MODULO_ASSISTIDO_LABEL,
  MODULO_LABEL,
  MOTOR_ASSISTIDO_LABEL,
  STATUS_QUESTAO_COR,
  nomeDaQuestao,
  rotuloDaSituacao,
  type ModuloAssistido,
} from '../../dominio';
import { TrechoEncontrado } from './TrechoEncontrado';
import {
  CabecalhoRedimensionavel,
  useLarguraDasColunas,
} from '../../components/colunasRedimensionaveis';
import { EnfileirarComentario } from '../../components/EnfileirarComentario';
import {
  FILTROS_VAZIOS,
  PainelDeFiltros,
  quantosFiltros,
  type FiltrosDeQuestoes,
} from './PainelDeFiltros';
import {
  lerConsulta,
  ORDENS,
  paginaDaLista,
  POR_PAGINA_PADRAO,
  TAMANHOS_DE_PAGINA,
} from './listaDeQuestoes';

/*
 * As larguras de partida. Elas viram o padrão para o qual o clique duplo na
 * divisa devolve, então precisam existir para todas as colunas - inclusive a do
 * enunciado, que antes era a única sem largura e absorvia a sobra.
 */
const LARGURA_PADRAO: Record<string, number> = {
  position: 70,
  statement: 460,
  exam: 220,
  module: 180,
  status: 150,
  editor: 180,
};
/** A coluna das caixas de marcar, no padrão do Ant Design. */
const LARGURA_DA_SELECAO = 32;

/**
 * O selo da sugestão, por módulo que a escreveu.
 *
 * **Era um selo só, "Sugestão", e ele deixou de responder a pergunta que o
 * revisor faz.** Enquanto a máquina apenas transcrevia prova, sugestão era
 * sempre a mesma coisa. Com o Comentário Assistido, o mesmo roxo passou a
 * cobrir dois trabalhos diferentes: conferir uma transcrição contra o PDF da
 * prova e conferir um argumento contra o acervo. Quem varria a lista só
 * descobria qual era qual abrindo a questão.
 *
 * **A distinção vai no rótulo, e não só na cor.** Cor separa quando já se sabe o
 * que ela significa, e ninguém aprende uma legenda que a tela não mostra - além
 * de não sobrar nada para quem não distingue roxo de magenta. O ícone é o
 * reforço: o documento é a prova transcrita, o balão é o comentário escrito.
 *
 * "Sugestão" continua sendo a cabeça do rótulo porque é o que decide se a
 * questão precisa de alguém: o módulo é o complemento, e diz de que revisão se
 * trata.
 */
const SELO_DA_SUGESTAO: Record<ModuloAssistido, { rotulo: string; cor: string }> = {
  cadastro: { rotulo: 'Sugestão · Cadastro', cor: 'purple' },
  comentario: { rotulo: 'Sugestão · Comentário', cor: 'magenta' },
};

const ICONE_DA_SUGESTAO: Record<ModuloAssistido, ReactNode> = {
  cadastro: <FileTextOutlined />,
  comentario: <CommentOutlined />,
};

/**
 * O ícone do item na lista de trabalho do Comentário Assistido.
 *
 * **A engrenagem girando não é enfeite: é o único estado da tela que muda
 * sozinho.** `reivindicada` quer dizer que um motor está escrevendo aquela
 * questão **agora**, e é a informação mais cara desta lista - quem edita à mão
 * nesse intervalo perde o trabalho, porque a sugestão entra por cima. Etiqueta
 * parada leria como mais um estado guardado no banco.
 *
 * O balão repete o do selo da sugestão de propósito: os dois falam do mesmo
 * módulo, e o ícone é o que amarra "está na lista" a "veio do Comentário
 * Assistido" sem gastar mais palavras na coluna.
 */
const ICONE_DA_LISTA: Partial<Record<EstadoDoItem, ReactNode>> = {
  pendente: <CommentOutlined />,
  reivindicada: <SyncOutlined spin />,
  aguardando_acervo: <ClockCircleOutlined />,
};

/**
 * O que o `Tooltip` do item da lista explica, por estado.
 *
 * O rótulo diz **onde a questão está**; o `Tooltip`, **o que isso significa
 * para quem ia editá-la agora** - que é a pergunta que traz alguém a esta tela.
 * A mensagem do próprio item, quando existe, ganha do texto fixo: ela nomeia a
 * fonte que falta, e é o que transforma "esperando acervo" em tarefa.
 */
const AVISO_DA_LISTA: Partial<Record<EstadoDoItem, string>> = {
  pendente: 'Na lista de trabalho do Comentário Assistido, esperando um motor.',
  reivindicada:
    'Um motor está escrevendo o comentário agora. Editar à mão vira trabalho perdido: a sugestão entra por cima.',
  aguardando_acervo:
    'O Comentário Assistido devolveu a questão: o acervo não tem o que fundamentá-la.',
};

/** Prévia em texto: a lista mostra o começo do enunciado, não a formatação. */
const emTexto = (html: string | null): string => {
  if (!html) return '';
  const texto = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return texto.length > 140 ? `${texto.slice(0, 140)}...` : texto;
};

/**
 * Lista de questões do backoffice.
 *
 * Mostra sempre a última versão salva, publicada ou não: quem está aqui é quem
 * escreve, e o rascunho de ontem precisa estar visível hoje.
 *
 * **No celular a tabela vira lista.** Sete colunas em 390px não encolhem, elas
 * quebram: o enunciado descia doze linhas, o nome da prova outras três e a
 * questão seguinte começava fora da tela. Rolagem horizontal resolveria o
 * desenho e não a leitura - ninguém rola de lado para escolher o que editar.
 *
 * **Todo texto é cortado, nunca embrulhado.** Enunciado em duas linhas, prova e
 * responsável em uma. Item de lista com altura imprevisível é lista que não dá
 * para varrer com o olho: a informação que identifica a questão está no começo
 * do enunciado, e o resto é o que se lê depois de abrir.
 *
 * Os filtros seguem o padrão do app: grade, rascunho aplicado no "Buscar" e
 * chips do que está valendo. Ver `PainelDeFiltros.tsx`.
 */
export function QuestoesPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const { filtrar, irParaPagina, busca } = useEstadoDeLista();
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [gavetaAberta, setGavetaAberta] = useState(false);

  /*
   * Filtros, ordem e página moram na URL: assim a tela sobrevive a sair e
   * voltar, a recarregar e a ser mandada por link. Ver `estado-de-lista.ts`.
   *
   * A leitura é a do `listaDeQuestoes`, a mesma que a tela da questão usa para
   * andar pela lista com "Anterior" e "Próxima" - uma definição só de qual é a
   * lista.
   */
  const consulta = lerConsulta(new URLSearchParams(busca));
  const { aplicados, termo, emRevisao, ordem, page, porPagina } = consulta;

  /*
   * Rascunho é o que está nos campos; aplicados é o que virou consulta. Só os
   * aplicados vão para a URL - rascunho é digitação em andamento, não é
   * endereço de nada. O que se manda por link é o que se está vendo.
   */
  const [rascunho, setRascunho] = useState<FiltrosDeQuestoes>(aplicados);

  /*
   * Voltar e avançar no navegador trocam os aplicados por fora. Sem isto os
   * campos continuariam mostrando o rascunho antigo enquanto a lista já é
   * outra - a tela contando duas histórias ao mesmo tempo.
   *
   * É o padrão de ajustar estado durante a renderização, e não num efeito: o
   * React descarta esta renderização e refaz com o valor novo, sem passar pela
   * tela intermediária errada.
   */
  const assinatura = JSON.stringify(aplicados);
  const [assinaturaDoRascunho, setAssinaturaDoRascunho] = useState(assinatura);
  if (assinatura !== assinaturaDoRascunho) {
    setAssinaturaDoRascunho(assinatura);
    setRascunho(aplicados);
  }

  const questoes = useQuery(paginaDaLista(consulta));

  const prova = useQuery({
    queryKey: ['exam', aplicados.examId],
    queryFn: () => api.getExam(aplicados.examId!),
    enabled: Boolean(aplicados.examId),
  });

  const quantidadeDeFiltros = quantosFiltros(aplicados);
  const semFiltro = quantidadeDeFiltros === 0;

  /** Escreve os filtros na URL e volta para a primeira página. */
  const escreverFiltros = (filtros: FiltrosDeQuestoes) => {
    filtrar({
      examId: filtros.examId,
      modules: filtros.modules,
      statuses: filtros.statuses,
      disciplineId: filtros.disciplineId,
      subjects: filtros.assuntos.subjectIds,
      details: filtros.assuntos.subjectDetailIds,
      responsavel: filtros.responsavel,
      fechadas: filtros.fechadas,
    });
    // Filtro novo, lista nova: continuar na página 7 mostraria outro pedaço,
    // ou página nenhuma.
    setSelecionadas([]);
  };

  const aplicar = () => {
    escreverFiltros(rascunho);
    setGavetaAberta(false);
  };

  const limpar = () => {
    setRascunho(FILTROS_VAZIOS);
    escreverFiltros(FILTROS_VAZIOS);
  };

  const painel = (
    <PainelDeFiltros
      rascunho={rascunho}
      aplicados={aplicados}
      nomeDaProva={prova.data?.name}
      onMudar={(mudancas) => setRascunho((atual) => ({ ...atual, ...mudancas }))}
      onAplicar={aplicar}
      onLimpar={limpar}
      // Fechar um chip refaz a busca sem ele; o rascunho acompanha pela
      // assinatura, que muda junto com a URL.
      onRemover={escreverFiltros}
      compacto={compacto}
    />
  );

  // Quem pode publicar. O backend decide de verdade; a tela só reflete.
  const { data: sessao } = useSession();
  const podePublicar = ['publisher', 'owner'].includes(
    (sessao?.user as { role?: string } | undefined)?.role ?? 'user',
  );

  /*
   * Amostra grátis em massa: liberar questão a questão numa prova inteira seria
   * dezenas de idas ao servidor, e um erro no meio deixaria metade marcada.
   */
  const marcarGratuitas = useMutation({
    mutationFn: (isFree: boolean) => api.setQuestionsFree(selecionadas, isFree),
    onSuccess: async (resultado, isFree) => {
      message.success(
        isFree
          ? `${resultado.alteradas} questão(ões) liberada(s) como amostra grátis.`
          : `${resultado.alteradas} questão(ões) voltaram a contar no limite semanal.`,
      );
      setSelecionadas([]);
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /*
   * Publicação em massa: publica a última versão de cada questão selecionada. As
   * que não podem (prova não publicada, anulada, sem novidade) são ignoradas, e
   * a mensagem diz quantas ficaram de fora.
   */
  const publicarSelecionadas = useMutation({
    mutationFn: () => api.publishQuestions(selecionadas),
    onSuccess: async ({ publicadas, ignoradas }) => {
      message.success(
        ignoradas > 0
          ? `${publicadas} questão(ões) publicada(s); ${ignoradas} ignorada(s) (prova não publicada, anulada ou sem versão nova).`
          : `${publicadas} questão(ões) publicada(s).`,
      );
      setSelecionadas([]);
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /*
   * "Voltar para rascunho" em vez de "despublicar": a coluna Situação já mostra
   * "Rascunho", então a ação que nomeia o estado de destino é lida sem tradução
   * mental - e "despublicar" não é palavra dicionarizada.
   */
  const voltarParaRascunho = useMutation({
    mutationFn: () => api.unpublishQuestions(selecionadas),
    onSuccess: async ({ despublicadas, ignoradas }) => {
      message.success(
        ignoradas > 0
          ? `${despublicadas} questão(ões) voltaram para rascunho; ${ignoradas} ignorada(s) (anulada ou já em rascunho).`
          : `${despublicadas} questão(ões) voltaram para rascunho.`,
      );
      setSelecionadas([]);
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const abrir = (questao: QuestionSummary) =>
    // A questão leva junto de onde veio, para o "voltar" da tela dela devolver
    // esta lista, nesta página, com estes filtros.
    void navigate(`/questoes/${questao.id}`, { state: { origem: busca } });

  const cadastrar = () =>
    void navigate(
      aplicados.examId ? `/questoes/nova?examId=${aplicados.examId}` : '/questoes/nova',
      { state: { origem: busca } },
    );

  const alternar = (id: string, marcada: boolean) =>
    setSelecionadas((atuais) =>
      marcada ? [...atuais, id] : atuais.filter((item) => item !== id),
    );

  /** As etiquetas de situação, iguais nos dois desenhos. */
  const etiquetas = (questao: QuestionSummary) => {
    /*
     * Sugestão ainda não revisada: o selo evita confundi-la com rascunho
     * escrito por uma pessoa, e diz qual módulo a escreveu.
     *
     * Sem `ingestionKind` a sugestão é antiga demais para dizer de onde veio -
     * então o selo genérico volta, em vez de a tela chutar um módulo.
     */
    const ehSugestao = questao.versionState === 'suggestion';
    const modulo = ehSugestao ? questao.ingestionKind : null;

    /*
     * O item aberto na lista de trabalho. Só os estados que o desenho conhece:
     * o backend manda o enum inteiro de propósito - estado de trabalho novo
     * aparece sozinho lá -, mas aqui só entra o que tem ícone e aviso, para não
     * pintar uma etiqueta sem explicação nenhuma.
     */
    const naLista =
      questao.commentJob && AVISO_DA_LISTA[questao.commentJob.status]
        ? questao.commentJob
        : null;

    return (
      <Space size={4} wrap>
        <Tag color={STATUS_QUESTAO_COR[questao.status]} style={{ marginInlineEnd: 0 }}>
          {rotuloDaSituacao(questao.status, questao.onAir)}
        </Tag>
        {modulo ? (
          /*
           * O motor fica no `Tooltip`, e não no rótulo: ele importa para
           * comparar a qualidade dos dois caminhos, não para escolher o que
           * revisar agora. Enfiá-lo na etiqueta encheria a coluna com o que
           * ninguém lê ao varrer a lista - e o rótulo sozinho já basta para
           * saber que revisão é aquela.
           */
          <Tooltip
            title={`Sugestão do ${MODULO_ASSISTIDO_LABEL[modulo]}${
              questao.ingestionEngine
                ? ` (${MOTOR_ASSISTIDO_LABEL[questao.ingestionEngine]})`
                : ''
            }. Ninguém revisou ainda.`}
          >
            <Tag
              color={SELO_DA_SUGESTAO[modulo].cor}
              icon={ICONE_DA_SUGESTAO[modulo]}
              style={{ marginInlineEnd: 0 }}
            >
              {SELO_DA_SUGESTAO[modulo].rotulo}
            </Tag>
          </Tooltip>
        ) : ehSugestao ? (
          <Tag color="purple" style={{ marginInlineEnd: 0 }}>
            Sugestão
          </Tag>
        ) : null}
        {/*
          Depois do selo, e não antes: o selo diz de onde veio o que está
          escrito, e isto diz o que ainda vai acontecer com a questão. Lidos
          nessa ordem, a linha conta a história inteira - o que é, quem
          escreveu, o que está em curso.
        */}
        {naLista ? (
          <Tooltip
            title={
              naLista.errorMessage ??
              `${AVISO_DA_LISTA[naLista.status]}${
                naLista.engine ? ` (${MOTOR_ASSISTIDO_LABEL[naLista.engine]})` : ''
              }`
            }
          >
            <Tag
              color={ESTADO_DA_LISTA_COR[naLista.status]}
              icon={ICONE_DA_LISTA[naLista.status]}
              style={{ marginInlineEnd: 0 }}
            >
              {ESTADO_DA_LISTA_LABEL[naLista.status]}
            </Tag>
          </Tooltip>
        ) : null}
        {questao.isFree ? (
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            Grátis
          </Tag>
        ) : null}
      </Space>
    );
  };

  const vazio = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        semFiltro ? 'Nenhuma questão cadastrada ainda.' : 'Nenhuma questão com esses filtros.'
      }
    >
      <Button type="primary" onClick={cadastrar}>
        Cadastrar questão
      </Button>
    </Empty>
  );

  /*
   * A lista do celular.
   *
   * A caixa de seleção fica fora da área que abre a questão: com as duas no
   * mesmo alvo, marcar cinco questões para publicar em massa viraria cinco
   * idas e vindas para a tela de edição.
   */
  const listaNoCelular = (
    <List
      loading={questoes.isPending}
      dataSource={questoes.data?.items ?? []}
      locale={{ emptyText: vazio }}
      renderItem={(questao) => (
        <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
          <Flex gap={10} style={{ width: '100%', minWidth: 0 }}>
            <Checkbox
              checked={selecionadas.includes(questao.id)}
              onChange={(evento) => alternar(questao.id, evento.target.checked)}
              style={{ marginTop: 2 }}
            />
            {/*
              `minWidth: 0` é o que faz o corte funcionar: sem ele o filho
              cresce com o texto e o item vaza para fora da tela em vez de
              truncar, que é o comportamento padrão do flex.
            */}
            <Flex
              vertical
              gap={2}
              style={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
              onClick={() => abrir(questao)}
            >
              <Flex align="center" gap={8} wrap>
                <Typography.Text strong>
                  {questao.position ? `Questão ${questao.position}` : 'Sem número'}
                </Typography.Text>
                {etiquetas(questao)}
              </Flex>

              {questao.essayKind ? (
                <Typography.Text strong ellipsis>
                  {nomeDaQuestao(questao)}
                </Typography.Text>
              ) : null}

              {emTexto(questao.statementHtml) ? (
                <Typography.Paragraph
                  type={questao.essayKind ? 'secondary' : undefined}
                  // Duas linhas: o suficiente para reconhecer a questão, e
                  // pouco o bastante para os itens terem a mesma altura.
                  ellipsis={{ rows: 2 }}
                  style={{ marginBottom: 0 }}
                >
                  {emTexto(questao.statementHtml)}
                </Typography.Paragraph>
              ) : (
                <Typography.Text type="secondary">Sem enunciado</Typography.Text>
              )}

              <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                {questao.examName} · {MODULO_LABEL[questao.module]}
                {questao.lastEditorId ? ` · ${questao.lastEditorName ?? 'Conta excluída'}` : ''}
              </Typography.Text>

              {/*
                Onde a busca casou. Sem isto a lista diz que a questão tem o
                termo mas não onde - e a citação quase nunca está no enunciado
                que aparece acima, e sim numa aba do comentário.
              */}
              {questao.trecho ? <TrechoEncontrado trecho={questao.trecho} /> : null}
            </Flex>
          </Flex>
        </List.Item>
      )}
    />
  );

  const colunas = useLarguraDasColunas('titularis:questoes:colunas', LARGURA_PADRAO);

  const tabela = (
    <Table
      rowKey="id"
      rowSelection={{
        selectedRowKeys: selecionadas,
        onChange: (chaves) => setSelecionadas(chaves as string[]),
      }}
      loading={questoes.isPending}
      dataSource={questoes.data?.items ?? []}
      /*
       * A soma das larguras, em número. Ver `colunasRedimensionaveis.tsx`: é o
       * que faz encolher uma coluna cortar o texto em vez de ser ignorado, e o
       * que faz arrastar alargar a tabela em vez de espremer a vizinha.
       *
       * `LARGURA_DA_SELECAO` é a coluna das caixas de marcar, que não é medida
       * pelo arrasto mas ocupa espaço: fora da conta, a tabela fica estreita
       * demais e a última coluna perde o fim.
       */
      scroll={{ x: colunas.total + LARGURA_DA_SELECAO }}
      /*
       * Explícito, e não herdado do `ellipsis` de uma coluna qualquer. O Antd
       * liga o layout fixo sozinho quando alguma coluna tem reticência, mas é
       * dele que o arrasto depende para valer: tirar a reticência de uma coluna
       * desligaria o redimensionamento de todas, sem nenhuma relação aparente
       * entre uma coisa e outra.
       */
      tableLayout="fixed"
      components={{ header: { cell: CabecalhoRedimensionavel } }}
      locale={{ emptyText: vazio }}
      pagination={false}
      onRow={(questao) => ({
        style: { cursor: 'pointer' },
        onClick: () => abrir(questao),
      })}
      columns={[
        {
          title: 'Nº',
          key: 'position',
          dataIndex: 'position',
          ...colunas.coluna('position'),
          render: (valor: number | null) => valor ?? '-',
        },
        {
          title: 'Questão',
          key: 'statement',
          dataIndex: 'statementHtml',
          ...colunas.coluna('statement'),
          render: (html: string | null, questao) => (
            <Space orientation="vertical" size={0}>
              {/* Nas discursivas o que identifica a questão é a parte da
                  prova: dissertação, peça prática ou questão discursiva. */}
              {questao.essayKind ? (
                <Typography.Text strong>{nomeDaQuestao(questao)}</Typography.Text>
              ) : null}
              {emTexto(html) ? (
                <Typography.Paragraph
                  type={questao.essayKind ? 'secondary' : undefined}
                  // Também cortado no desktop: sem isto, uma questão com
                  // enunciado longo tem o triplo da altura da vizinha e a
                  // tabela deixa de ser varrível.
                  ellipsis={{ rows: 2 }}
                  style={{ marginBottom: 0 }}
                >
                  {emTexto(html)}
                </Typography.Paragraph>
              ) : (
                <Typography.Text type="secondary">Sem enunciado</Typography.Text>
              )}
              {/*
                O trecho da busca vem abaixo do enunciado, e não no lugar dele:
                os dois respondem coisas diferentes - que questão é esta, e por
                que ela apareceu nesta busca.
              */}
              {questao.trecho ? <TrechoEncontrado trecho={questao.trecho} /> : null}
            </Space>
          ),
        },
        {
          title: 'Prova',
          key: 'exam',
          dataIndex: 'examName',
          ellipsis: true,
          ...colunas.coluna('exam'),
        },
        {
          title: 'Módulo',
          key: 'module',
          dataIndex: 'module',
          // Reticência aqui também: a coluna agora encolhe de verdade, e sem
          // isto "Registro de Imóveis" vazaria para cima da vizinha.
          ellipsis: true,
          ...colunas.coluna('module'),
          render: (valor: ExamModule) => MODULO_LABEL[valor],
        },
        {
          title: 'Situação',
          key: 'status',
          dataIndex: 'status',
          ...colunas.coluna('status'),
          render: (_valor: QuestionStatus, questao) => etiquetas(questao),
        },
        {
          // Quem publicou a última versão ou, se ainda é rascunho, quem a salvou.
          title: 'Responsável',
          key: 'editor',
          dataIndex: 'lastEditorName',
          ellipsis: true,
          ...colunas.coluna('editor'),
          render: (nome: string | null, questao) =>
            questao.lastEditorId ? (
              // Autor com a conta excluída aparece assim; versão antiga sem
              // autor registrado, como "—".
              (nome ?? 'Conta excluída')
            ) : questao.ingestionKind ? (
              /*
               * **"—" dizia "ninguém", e a resposta certa é o módulo.**
               *
               * Esta coluna é a mesma coisa que o filtro de Responsável, onde
               * "Cadastro Assistido" e "Comentário Assistido" são opções ao lado
               * das pessoas. Filtrar por um deles e receber uma coluna inteira
               * de travessão é a tela mostrando um conjunto e o filtro dizendo
               * outro - a divergência que a expressão do responsável existe para
               * impedir.
               *
               * Em cinza porque não é gente: ninguém assumiu esta questão ainda.
               */
              <Typography.Text type="secondary">
                {MODULO_ASSISTIDO_LABEL[questao.ingestionKind]}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            ),
        },
      ]}
    />
  );

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      {/*
        No celular o título e o botão empilham. Lado a lado com 390px, "Cadastrar
        questão" espremia o título até ele quebrar em três linhas.
      */}
      <Flex
        align={compacto ? 'stretch' : 'start'}
        justify="space-between"
        gap={12}
        vertical={compacto}
      >
        <div style={{ minWidth: 0 }}>
          <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
            Questões
          </Typography.Title>
          <Typography.Text type="secondary">
            {emRevisao
              ? /*
                 * "do Cadastro Assistido" era fixo, e a fila deixou de ser só
                 * dele: o Comentário Assistido escreve na mesma fila. Qual
                 * módulo escreveu cada uma é o selo de cada linha que diz.
                 */
                'Sugestões da máquina aguardando revisão. Nenhuma está publicada.'
              : prova.data
                ? `Filtrando pela prova ${prova.data.name}.`
                : 'Escolha uma prova para cadastrar questões nela.'}
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={cadastrar} block={compacto}>
          Cadastrar questão
        </Button>
      </Flex>

      {questoes.error ? (
        <Alert type="error" showIcon title={(questoes.error as Error).message} />
      ) : null}

      {emRevisao ? (
        <Alert
          type="info"
          showIcon
          title="Fila de revisão"
          description="Abra cada questão para conferir e ajustar antes de publicar. Selecione várias para publicar de uma vez. O selo de cada linha diz se o texto veio do Cadastro Assistido, que transcreve a prova, ou do Comentário Assistido, que escreve a partir do acervo."
          action={
            <Button size="small" onClick={() => filtrar({ revisao: undefined })}>
              Ver todas
            </Button>
          }
        />
      ) : null}

      {/*
        A busca vive fora do painel de filtros e fora do rascunho.

        Fora do painel porque é o caminho principal para achar uma questão, não
        mais um recorte: no celular ela ficaria atrás da gaveta, e procurar
        exigiria abrir, digitar, aplicar e fechar. Fora do rascunho porque busca
        se dispara no Enter, e não junto de um lote de filtros - quem digita uma
        palavra espera resultado, não um botão a mais.

        A convenção fica escrita no campo, e não num tooltip: é regra de
        sintaxe, e regra que só aparece ao passar o mouse não existe para quem
        digita direto - nem para quem usa o celular.
      */}
      <Input.Search
        allowClear
        size="large"
        enterButton
        defaultValue={termo}
        placeholder='Palavra ou frase - use "aspas" para a ordem exata'
        onSearch={(valor) => {
          filtrar({ termo: valor.trim() || undefined });
          setSelecionadas([]);
        }}
      />

      {/*
        No desktop o painel fica aberto; no celular ele mora numa gaveta, como
        no app. Oito filtros empilhados ocupariam a tela inteira e empurrariam
        a lista para baixo da dobra - a tela abriria mostrando o formulário de
        busca em vez do resultado.
      */}
      {compacto ? (
        <Flex gap={8} align="center" wrap>
          <Badge count={quantidadeDeFiltros}>
            <Button icon={<FilterOutlined />} onClick={() => setGavetaAberta(true)}>
              Filtros
            </Button>
          </Badge>
          <Select
            style={{ flex: 1, minWidth: 150 }}
            value={ordem ?? 'prova'}
            onChange={(valor) => filtrar({ ordem: valor === 'prova' ? undefined : valor })}
            options={ORDENS}
          />
          <Drawer
            title="Filtrar questões"
            placement="bottom"
            size="auto"
            open={gavetaAberta}
            onClose={() => setGavetaAberta(false)}
          >
            {painel}
          </Drawer>
        </Flex>
      ) : (
        <Card
          size="small"
          title="Filtrar questões"
          /*
           * Ordem fica fora do painel, e aplica na hora: filtro muda quais
           * questões existem, e por isso espera o "Buscar"; ordem só muda onde
           * elas estão na tela. Enfiá-la na gaveta faria "ordenar pela mais
           * recente" custar abrir, escolher, aplicar e fechar.
           */
          extra={
            <Select
              style={{ width: 210 }}
              value={ordem ?? 'prova'}
              onChange={(valor) => filtrar({ ordem: valor === 'prova' ? undefined : valor })}
              options={ORDENS}
            />
          }
        >
          {painel}
        </Card>
      )}

      <Card size="small">
        {selecionadas.length > 0 ? (
          <Flex align="center" gap={8} wrap style={{ marginBottom: 12 }}>
            <Typography.Text strong>{selecionadas.length} selecionada(s)</Typography.Text>
            <Tooltip title={podePublicar ? '' : 'Seu nível de acesso não permite publicar questões.'}>
              <Popconfirm
                title="Publicar as selecionadas?"
                description="As que puderem publicar ficam visíveis para os alunos. Prova não publicada, anulada ou sem versão nova são ignoradas."
                okText="Publicar"
                cancelText="Cancelar"
                disabled={!podePublicar}
                onConfirm={() => publicarSelecionadas.mutate()}
              >
                <Button
                  size="small"
                  type="primary"
                  loading={publicarSelecionadas.isPending}
                  disabled={!podePublicar}
                >
                  Publicar
                </Button>
              </Popconfirm>
            </Tooltip>
            <Tooltip title={podePublicar ? '' : 'Seu nível de acesso não permite publicar questões.'}>
              <Popconfirm
                title="Voltar as selecionadas para rascunho?"
                description="Elas saem do ar para os alunos. O histórico das versões publicadas continua intacto. Anuladas e as que já são rascunho ficam de fora."
                okText="Voltar para rascunho"
                cancelText="Cancelar"
                disabled={!podePublicar}
                onConfirm={() => voltarParaRascunho.mutate()}
              >
                <Button size="small" loading={voltarParaRascunho.isPending} disabled={!podePublicar}>
                  Voltar para rascunho
                </Button>
              </Popconfirm>
            </Tooltip>
            <Button
              size="small"
              loading={marcarGratuitas.isPending}
              onClick={() => marcarGratuitas.mutate(true)}
            >
              Liberar grátis
            </Button>
            <Button
              size="small"
              loading={marcarGratuitas.isPending}
              onClick={() => marcarGratuitas.mutate(false)}
            >
              Remover grátis
            </Button>
            {/*
              Sem confirmação, e de propósito: enfileirar não aciona nada nem
              gasta - a questão fica disponível até alguém escolher (D-24). O
              diálogo que abre é para escolher as abas, que é o que o backend
              exige.
            */}
            {/*
              A seleção **não** é limpa ao enfileirar: quem acabou de mandar
              vinte questões para a lista costuma querer a mesma seleção para o
              passo seguinte, e limpá-la obrigava a remarcar tudo.
            */}
            <EnfileirarComentario questionIds={selecionadas} />
            <Button size="small" type="text" onClick={() => setSelecionadas([])}>
              Limpar seleção
            </Button>
          </Flex>
        ) : null}

        {/*
          A saída para quem se perdeu nas larguras. Só aparece se houver o que
          desfazer: um botão permanente sugeriria que a tabela vem torta de
          fábrica. No celular não existe, porque lá não há tabela.
        */}
        {!compacto && colunas.personalizada ? (
          <Flex justify="flex-end" style={{ marginBottom: 8 }}>
            <Button size="small" type="text" icon={<ColumnWidthOutlined />} onClick={colunas.restaurar}>
              Restaurar larguras
            </Button>
          </Flex>
        ) : null}

        {compacto ? listaNoCelular : tabela}

        {/*
          A paginação sai de dentro da tabela para servir aos dois desenhos.
          Fica visível mesmo com uma página só: escondê-la levaria junto o
          seletor de tamanho, e quem tivesse posto 100 por página numa lista de
          30 perderia o controle para voltar a 20 - só editando a URL na mão.
        */}
        <Flex justify={compacto ? 'center' : 'flex-end'} style={{ marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={questoes.data?.pageSize ?? porPagina}
            total={questoes.data?.total ?? 0}
            size={compacto ? 'small' : undefined}
            showSizeChanger
            pageSizeOptions={TAMANHOS_DE_PAGINA}
            /*
             * Trocar de página e trocar o tamanho chegam os dois por aqui, e
             * é de propósito que `onShowSizeChange` não seja usado junto: na
             * troca de tamanho o Ant Design dispara os dois callbacks, cada um
             * escreveria na URL na mesma renderização, e a segunda escrita -
             * que só conhece a página - apagaria o tamanho novo. Uma escrita
             * por interação.
             */
            onChange={(proxima, tamanho) => {
              if (tamanho !== porPagina) {
                // Mudar o tamanho muda quais questões cabem na página, então a
                // lista recomeça: continuar na página 7 mostraria outro pedaço.
                filtrar({ porPagina: tamanho === POR_PAGINA_PADRAO ? undefined : tamanho });
                return;
              }
              irParaPagina(proxima);
            }}
          />
        </Flex>
      </Card>
    </Space>
  );
}
